import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Target, Users2, Flag, CalendarDays, Star, Phone, ArrowRight, Trophy, Bell, UserCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtData } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { waNumero } from "@/lib/utils";
import { pushSupportato, eIscritto } from "@/lib/pushNotifiche";

function Riga({ testo }) {
  const righe = (testo || "").split("\n").map((r) => r.trim()).filter(Boolean);
  if (righe.length <= 1) return <p className="text-sm text-slate-700 font-semibold">{righe[0] || ""}</p>;
  return (
    <ul className="space-y-1">
      {righe.slice(0, 6).map((r, i) => (
        <li key={i} className="text-sm text-slate-700 font-semibold flex gap-2"><span className="text-amber-400 shrink-0">•</span><span className="min-w-0">{r}</span></li>
      ))}
      {righe.length > 6 && <li className="text-xs text-slate-400 font-semibold pl-4">…e altro</li>}
    </ul>
  );
}

export default function HomeAnimatore() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [classifica, setClassifica] = useState(null);
  const [notifOff, setNotifOff] = useState(false);

  const load = useCallback(() => {
    api.get("/dashboard-animatore").then(async (r) => {
      setD(r.data);
      if (r.data.classifica_pubblica && r.data.squadra) {
        try {
          const cl = await api.get("/classifica");
          const riga = cl.data.classifica.find((s) => s.id === r.data.squadra.id);
          setClassifica(riga || null);
        } catch { setClassifica(null); }
      }
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  // Promemoria notifiche: solo se supportate ma non attive su questo dispositivo
  useEffect(() => {
    (async () => {
      if (pushSupportato()) setNotifOff(!(await eIscritto()));
    })();
  }, []);

  if (!d) return <p className="text-slate-400 font-semibold" data-testid="home-animatore-loading">Caricamento...</p>;

  const promemoria = [];
  if (d.profilo_incompleto.length > 0)
    promemoria.push({ testo: `Completa il profilo: manca ${d.profilo_incompleto.join(", ")}.`, to: "/profilo", icon: UserCircle });
  if (notifOff)
    promemoria.push({ testo: "Attiva le notifiche per ricevere il programma e gli avvisi.", to: "/profilo", icon: Bell });

  return (
    <div data-testid="home-animatore-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Ciao, {user.nome}! 👋</h1>
      <p className="text-sm text-slate-500 font-semibold mb-6">Ecco cosa ti riguarda oggi.</p>

      {/* Promemoria (se ce ne sono) */}
      {promemoria.map((p, i) => (
        <Link key={i} to={p.to} className="card-app p-4 mb-3 flex items-center gap-3 border border-amber-200 bg-amber-50/50 hover:shadow-md transition-all block" data-testid={`promemoria-${i}`}>
          <p.icon className="h-5 w-5 text-amber-500 shrink-0" />
          <p className="text-sm font-bold text-amber-800 flex-1 min-w-0">{p.testo}</p>
          <ArrowRight className="h-4 w-4 text-amber-400 shrink-0" />
        </Link>
      ))}

      <div className="grid md:grid-cols-2 gap-4">
        {d.giorno_attivo ? (
          <>
            {/* Programma di oggi */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5 md:col-span-2" data-testid="card-programma">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="font-display font-extrabold text-slate-900 flex items-center gap-2"><ClipboardList className="h-5 w-5 text-amber-500" /> Programma di oggi</p>
                <Link to="/programma" className="text-xs font-bold text-amber-600 shrink-0">Apri →</Link>
              </div>
              {d.programma_oggi ? (
                <>
                  <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wide mb-1.5">{d.programma_oggi.gruppo_nome}</p>
                  <Riga testo={d.programma_oggi.testo} />
                </>
              ) : (
                <p className="text-sm text-slate-400 font-semibold">Il tuo gruppo non ha ancora pubblicato il programma di oggi.</p>
              )}
            </motion.div>

            {/* Postazione di oggi */}
            {d.postazioni_oggi.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5" data-testid="card-postazione">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <p className="font-display font-extrabold text-slate-900 flex items-center gap-2"><Target className="h-5 w-5 text-sky-500" /> Sei di presidio oggi</p>
                  <Link to="/mia-postazione" className="text-xs font-bold text-amber-600 shrink-0">Apri →</Link>
                </div>
                <div className="space-y-2">
                  {d.postazioni_oggi.map((p, i) => (
                    <div key={i} className="rounded-2xl bg-slate-50 p-3">
                      <p className="font-bold text-slate-800 text-sm">{p.nome}</p>
                      <p className="text-xs text-slate-400 font-semibold">Gioco: {p.gioco_nome || "da definire"}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5 md:col-span-2 border border-slate-200 bg-slate-50/60" data-testid="card-operativita-non-attiva">
            <p className="font-display font-extrabold text-slate-900 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-slate-400" /> Operatività sospesa</p>
            <p className="text-sm text-slate-500 font-semibold mt-2">Programma, presenze e postazioni saranno disponibili durante il periodo dell'oratorio e nei giorni attivi.</p>
          </motion.div>
        )}

        {/* La mia squadra */}
        {d.squadra && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5" data-testid="card-squadra">
            <p className="font-display font-extrabold text-slate-900 flex items-center gap-2 mb-3"><Flag className="h-5 w-5 text-emerald-500" /> La mia squadra</p>
            <div className="flex items-center gap-3">
              <span className="h-6 w-6 rounded-full shrink-0 border-2 border-white shadow" style={{ background: d.squadra.colore || "#94a3b8" }} />
              <p className="font-display font-extrabold text-slate-900 text-lg flex-1 truncate">{d.squadra.nome}</p>
            </div>
            {d.classifica_pubblica && classifica ? (
              <Link to="/classifica" className="mt-3 flex items-center gap-2 rounded-2xl bg-amber-50 p-3 hover:bg-amber-100 transition-colors" data-testid="squadra-classifica">
                <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="text-sm font-bold text-amber-800 flex-1">{classifica.posizione}° posto · {classifica.punti} pt</span>
                <ArrowRight className="h-4 w-4 text-amber-400" />
              </Link>
            ) : (
              <p className="text-xs text-slate-400 font-semibold mt-2">La classifica non è ancora pubblica.</p>
            )}
          </motion.div>
        )}

        {/* Il mio gruppo + referenti */}
        {d.gruppo && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5" data-testid="card-gruppo">
            <p className="font-display font-extrabold text-slate-900 flex items-center gap-2 mb-3"><Users2 className="h-5 w-5 text-amber-500" /> Il mio gruppo</p>
            <p className="font-bold text-slate-800 mb-2">{d.gruppo.nome}</p>
            {d.gruppo.referenti.length > 0 && (
              <>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wide mb-1.5">{d.gruppo.referenti.length === 1 ? "Referente" : "Referenti"}</p>
                {/* Chiamare è l'azione primaria, WhatsApp la secondaria.
                    Prima l'unico pulsante apriva WhatsApp: un servizio di terzi
                    a cui, per contattare un responsabile, si consegnava il suo
                    numero — e che non tutti hanno installato. `tel:` funziona
                    su qualunque telefono e non coinvolge nessuno. */}
                <div className="space-y-1.5">
                  {d.gruppo.referenti.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Star className="h-3.5 w-3.5 text-emerald-500 fill-current shrink-0" />
                      <span className="font-semibold text-slate-700 flex-1 min-w-0 truncate">{r.nome} {r.cognome}</span>
                      {r.telefono ? (
                        <>
                          <a href={`tel:${r.telefono}`}
                            className="text-emerald-600 hover:text-emerald-700 shrink-0"
                            aria-label={`Chiama ${r.nome}`} data-testid={`referente-tel-${i}`}>
                            <Phone className="h-4 w-4" />
                          </a>
                          <a href={`https://wa.me/${waNumero(r.telefono)}`} target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-slate-500 shrink-0 text-[11px] font-bold"
                            aria-label={`WhatsApp ${r.nome}`} data-testid={`referente-wa-${i}`}>
                            WhatsApp
                          </a>
                        </>
                      ) : (
                        <span className="text-[11px] font-semibold text-slate-400 shrink-0">
                          contattalo di persona
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* Prossime attività */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card-app p-5" data-testid="card-attivita">
          <div className="flex items-center justify-between gap-2 mb-3">
            <p className="font-display font-extrabold text-slate-900 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-sky-500" /> Prossime attività</p>
            <Link to="/calendario" className="text-xs font-bold text-amber-600 shrink-0">Calendario →</Link>
          </div>
          {d.prossime_attivita.length === 0 ? (
            <p className="text-sm text-slate-400 font-semibold">Nessuna attività in programma.</p>
          ) : (
            <div className="space-y-2">
              {d.prossime_attivita.map((a, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="text-center shrink-0 w-11">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">{fmtData(a.data).slice(0, 5)}</p>
                    <p className="font-display font-extrabold text-slate-700 text-sm leading-none">{a.ora_inizio}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{a.titolo}</p>
                    {a.luogo && <p className="text-xs text-slate-400 font-semibold truncate">{a.luogo}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
